import React, { useEffect } from "react";
import {
  Dialog as MUIDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Grid,
  Checkbox,
  IconButton,
} from "@mui/material";
import isEqual from "lodash/isEqual";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import { ColorPicker } from "component/atoms/color-picker";
import { enqueueSnackbar } from "notistack";
import { zohoApi } from "zohoApi";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useModuleArray } from "hook/useModuleArray";
import { useModuleFields } from "hook/useModuleFields";
import { useZohoAuthContext } from "context/ZohoAuthContext";
import { CustomTextfield2 } from "component/atoms/custom-textfield-2";
import { DndAutocomplete2 } from "component/atoms/dnd-autocomplete2";

const palette = {
  tomato: "#D50000",
  flamingo: "#E67C73",
  tangerine: "#F4511E",
  banana: "#F6BF26",
  sage: "#33B679",
  basil: "#0B8043",
  peacock: "#039BE5",
  blueberry: "#3F51B5",
  lavender: "#7986CB",
  grape: "#8E24AA",
  graphite: "#616161",
};

export function EventSettingDialog({ dialogOpen, handleCloseDialog, savedSetting }) {
  const queryClient = useQueryClient();
  const moduleArray = useModuleArray();
  const { orgId, apiKey, dataCenterUrl } = useZohoAuthContext();

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = React.useState(false);

  const { mutate } = useMutation({
    mutationKey: ["update-item"],
    mutationFn: async (data) => {
      const { data: respData } = await zohoApi.config.upsertConfigToDB({
        orgId,
        apiKey,
        dataCenterUrl,
        settingId: data?.setting_id,
        settingApiName: "eventConfig",
        settingSchema: data,
      });
      return respData;
    },
    onSuccess: (data) => {
      if (data?.data?.setting_id) {
        queryClient.invalidateQueries({ queryKey: ["eventConfigArray"] });
        handleCloseDialog();
        enqueueSnackbar("Successfull", { variant: "success" });
      }
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { dirtyFields },
    reset,
  } = useForm({
    defaultValues: {
      setting_id: savedSetting?.setting_id ?? "",
      activity: savedSetting?.activity ?? "",
      title: savedSetting?.title ?? "",
      module: savedSetting?.module ?? {},
      startTimeField: savedSetting?.startTimeField ?? {},
      endTimeField: savedSetting?.endTimeField ?? {},
      allDay: savedSetting?.allDay ?? false,
      popupFields: savedSetting?.popupFields ?? [],
      eventColor: savedSetting?.eventColor ?? palette.lavender,
      enabledSetting: savedSetting?.enabledSetting ?? true,
      deletable: savedSetting?.deletable ?? true,
      dragToMove: savedSetting?.dragToMove ?? false,
      dragToResize: savedSetting?.dragToResize ?? false,
    },
  });
  const startTimeSystemMandatory = getValues("startTimeField")?.system_mandatory === true;
  const endTimeSystemMandatory = getValues("endTimeField")?.system_mandatory === true;
  const allDaySelected = getValues("allDay");
  // console.log({ startTimeSystemMandatory, endTimeSystemMandatory, allDaySelected });

  const disableDragToResize = allDaySelected || startTimeSystemMandatory || endTimeSystemMandatory;

  const moduleApiName = getValues("module")?.api_name;
  const { moduleFields, isFetching } = useModuleFields({ moduleApiName });

  const systemMandatoryTrue = moduleFields?.systemMandatoryTrue || [];
  const systemMandatoryFalse = moduleFields?.systemMandatoryFalse || [];
  const dateAndDatetimeFields = moduleFields?.dateAndDatetimeFields || [];
  // console.log({ dateAndDatetimeFields });

  useEffect(() => {
    const popupFieldsLength = getValues("popupFields").length;
    const systemMandatoryTrueLength = systemMandatoryTrue.length;

    if (systemMandatoryTrueLength > 0 && popupFieldsLength === 0) {
      setValue("popupFields", systemMandatoryTrue, { shouldDirty: true });
    }
  }, [systemMandatoryTrue]);

  const onSubmit = async (data) => {
    // console.log(getValues());
    // return;
    mutate(data);
  };

  return (
    <>
      <MUIDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        fullWidth={true}
        slotProps={{
          paper: { ...(isFetching ? { sx: { position: "relative" } } : {}), component: "form", onSubmit: handleSubmit(onSubmit) },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <span>{savedSetting ? getValues("activity") : "New Event Setting"}</span>
          {savedSetting ? (
            <IconButton
              aria-label="delete"
              size="small"
              color="error"
              onClick={() => {
                setOpenDeleteConfirmation(true);
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          ) : null}
        </DialogTitle>
        <DialogContent
          // id="esdc"
          sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
        >
          <Grid container>
            <Grid size={4}>
              <Typography component="label" htmlFor="activity-input" sx={{ color: "text.secondary" }}>
                Activity
              </Typography>
            </Grid>
            <Grid size={8}>
              <Controller
                name="activity"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    sx={{ width: "100%" }}
                    id="activity-input"
                    placeholder="Enter activity type"
                    variant="standard"
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid size={4}>
              <Typography component="label" htmlFor="module-input" sx={{ color: "text.secondary" }}>
                Modules
              </Typography>
            </Grid>
            <Grid size={8}>
              <Controller
                name="module"
                control={control}
                render={({ field }) => {
                  return (
                    <Autocomplete
                      {...field}
                      id="module-input"
                      filterSelectedOptions
                      disabled={!moduleArray}
                      options={moduleArray}
                      getOptionLabel={(option) => option?.plural_label || ""}
                      onChange={async (e, value) => {
                        const currentModuleApiName = value?.api_name;
                        const previousModuleApiName = getValues("module")?.api_name;

                        if (currentModuleApiName === previousModuleApiName) {
                          return;
                        }

                        field.onChange(value);
                        setValue("startTimeField", {}, { shouldDirty: true });
                        setValue("endTimeField", {}, { shouldDirty: true });
                        setValue("popupFields", [], { shouldDirty: true });
                        setValue("title", "", { shouldDirty: true });
                        watch("module");
                      }}
                      size="small"
                      renderInput={(params) => <TextField {...params} required placeholder="Modules" variant="standard" />}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid size={4}>
              <Typography component="label" htmlFor="title-input" sx={{ color: "text.secondary" }}>
                Title
              </Typography>
            </Grid>
            <Grid size={8}>
              <CustomTextfield2
                Controller={Controller}
                control={control}
                label="Title"
                allFields={[...systemMandatoryTrue, ...systemMandatoryFalse]}
                entity={moduleApiName}
                onChangeText={(value) => {
                  setValue("title", value, { shouldDirty: true });
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid size={4}>
              <Typography component="label" htmlFor="start-time-input" sx={{ color: "text.secondary" }}>
                Start Time Field
              </Typography>
            </Grid>
            <Grid size={8}>
              <Controller
                name="startTimeField"
                control={control}
                render={({ field }) => {
                  const { onChange, ...rest } = field;
                  return (
                    <Autocomplete
                      {...rest}
                      id="start-time-input"
                      disabled={!dateAndDatetimeFields}
                      options={dateAndDatetimeFields}
                      getOptionLabel={(option) => option?.field_label || ""}
                      onChange={(e, value, reason) => {
                        onChange(value);
                        const allDay = isEqual(getValues("endTimeField"), value);
                        setValue("allDay", allDay, { shouldDirty: true });
                      }}
                      filterSelectedOptions
                      size="small"
                      renderInput={(params) => <TextField {...params} required placeholder="Start Time Field" variant="standard" />}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid size={4}>
              <Typography component="label" htmlFor="end-time-input" sx={{ color: "text.secondary" }}>
                End Time Field
              </Typography>
            </Grid>
            <Grid size={8}>
              <Controller
                name="endTimeField"
                control={control}
                render={({ field }) => {
                  const { onChange, ...rest } = field;
                  return (
                    <Autocomplete
                      {...rest}
                      id="end-time-input"
                      sx={{ flexGrow: 1 }}
                      disabled={!dateAndDatetimeFields}
                      options={dateAndDatetimeFields}
                      getOptionLabel={(option) => option?.field_label || ""}
                      onChange={(e, value, reason) => {
                        onChange(value);
                        const allDay = isEqual(getValues("startTimeField"), value);
                        setValue("allDay", allDay, { shouldDirty: true });
                      }}
                      filterSelectedOptions
                      size="small"
                      renderInput={(params) => <TextField {...params} required placeholder="End Time Field" variant="standard" />}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid size={4}>
              <Typography component="label" htmlFor="all-day" sx={{ color: "text.secondary" }}>
                All day
              </Typography>
            </Grid>
            <Grid size={8}>
              <Controller
                name="allDay"
                control={control}
                render={({ field }) => {
                  const { value, onChange, ...rest } = field;
                  return (
                    <Checkbox
                      {...rest}
                      id="all-day"
                      onChange={(e, checked) => {
                        onChange(e);
                        if (checked) {
                          const startTimeFieldValue = getValues("startTimeField");
                          setValue("endTimeField", startTimeFieldValue, { shouldDirty: true });
                          setValue("dragToResize", false, { shouldDirty: true });
                          return;
                        }
                        setValue("endTimeField", {}, { shouldDirty: true });
                      }}
                      disableRipple
                      disableFocusRipple
                      size="small"
                      checked={value}
                      sx={{ py: "4px", pl: 0 }}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>

          <Grid container>
            <Grid size={4}>
              <Typography
                component="label"
                sx={{ color: "text.secondary" }}
                onClick={() => {
                  const searchFieldButton = document.getElementById("search-field-button");
                  searchFieldButton?.click();
                }}
              >
                Popup Fields
              </Typography>
            </Grid>
            <Grid size={8}>
              <DndAutocomplete2
                systemMandatoryTrue={systemMandatoryTrue}
                allFields={[...systemMandatoryTrue, ...systemMandatoryFalse]}
                savedPopupField_Ids={watch("popupFields")?.map((field) => field.id)}
                handleSave={(value) => {
                  setValue("popupFields", value, { shouldDirty: true });
                }}
              />
              <ul style={{ margin: 0, paddingInlineStart: "1.25rem" }}>
                {watch("popupFields")?.map((field) => {
                  return <li>{field.field_label}</li>;
                })}
              </ul>
            </Grid>
          </Grid>

          <Grid container>
            <Grid size={4}>
              <Typography
                component="label"
                sx={{ color: "text.secondary" }}
                onClick={() => {
                  const colorPickerButton = document.getElementById("color-picker-button");
                  colorPickerButton?.click();
                }}
              >
                Event color
              </Typography>
            </Grid>
            <Grid size={8}>
              <ColorPicker
                palette={palette}
                value={getValues("eventColor")}
                handleValue={(value) => {
                  setValue("eventColor", value, { shouldDirty: true });
                }}
              />
            </Grid>
          </Grid>

          <FormControl component="fieldset" variant="standard">
            <FormLabel component="legend">Assign responsibility</FormLabel>
            <FormGroup>
              <Box sx={{ display: "flex" }}>
                <Controller
                  name="enabledSetting"
                  control={control}
                  render={({ field }) => {
                    const { value, ...rest } = field;
                    return <FormControlLabel sx={{ flex: 1 }} label="Enabled activity" control={<Switch {...rest} checked={value} />} />;
                  }}
                />
                <Controller
                  name="deletable"
                  control={control}
                  render={({ field }) => {
                    const { value, ...rest } = field;
                    return <FormControlLabel sx={{ flex: 1 }} label="Can be deleted" control={<Switch {...rest} checked={value} />} />;
                  }}
                />
              </Box>
              <Box sx={{ display: "flex" }}>
                <Controller
                  name="dragToMove"
                  control={control}
                  render={({ field }) => {
                    const { value, ...rest } = field;
                    return <FormControlLabel sx={{ flex: 1 }} label="Drag To Move" control={<Switch {...rest} checked={value} />} />;
                  }}
                />
                <Controller
                  name="dragToResize"
                  control={control}
                  render={({ field }) => {
                    const { value, ...rest } = field;
                    return (
                      <FormControlLabel
                        disabled={disableDragToResize}
                        sx={{ flex: 1 }}
                        label="Drag To Resize"
                        control={<Switch {...rest} checked={value} />}
                      />
                    );
                  }}
                />
              </Box>
            </FormGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              handleCloseDialog();
              reset();
            }}
            size="small"
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          >
            Cancel
          </Button>
          <Button type="submit" size="small" variant="contained" sx={{ textTransform: "capitalize" }}>
            Save
          </Button>
        </DialogActions>
        {isFetching ? <Box sx={{ bgcolor: "rgba(236, 236, 236, .3)", position: "absolute", inset: 0 }} /> : null}
      </MUIDialog>
      <MUIDialog open={openDeleteConfirmation}>
        <DialogContent sx={{ pb: "4px" }}>Are you sure?</DialogContent>
        <DialogContent sx={{ pt: 0 }}>This action is permanent.</DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteConfirmation(false);
            }}
            size="small"
            variant="outlined"
            sx={{ textTransform: "capitalize" }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              const { data } = await zohoApi?.config?.deleteConfigFromDB({
                orgId,
                apiKey,
                dataCenterUrl,
                settingId: getValues("setting_id"),
              });
              enqueueSnackbar(data?.message, { variant: "success" });
              setOpenDeleteConfirmation(false);
              queryClient.invalidateQueries({ queryKey: ["eventConfigArray"] });
              handleCloseDialog();
            }}
            size="small"
            variant="contained"
            sx={{ textTransform: "capitalize" }}
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </MUIDialog>
    </>
  );
}
